/* eslint-disable @typescript-eslint/no-explicit-any */
import {Box} from '@mui/material';
import {cloneDeep} from 'lodash';
import {GetStaticPropsContext} from 'next';
import ErrorPage from 'next/error';
import React from 'react';
import {BlockRenderer} from '~/components/renderer/block-renderer';
import {GLOBAL_TYPE} from '~/constants/content-map-type';
import {usePageSetting} from '~/providers/page-context.provider';
import {handleConvertToLocaleContentful} from '~/utils';
import {getGloablByType} from '~/utils/global-operations';
import {getPageData} from '../[[...slug]]/index.page';
import {LIST_ID_GAME_OUR_PICK, REVALIDATE_TIME} from '~/constants';
import getListGameForNav from '~/components/wrapper/organisms/navigation-bar/get-list-game';
import {getLitGameByIdBlock} from '~/components/wrapper/organisms/navigation-bar/get-list-game-by-id';
import getListTitleGame from '~/pages/games/get-list-title-game';
import getParamFilterBy from '~/pages/games/get-param-filter-by';

type SupportQExt = {
  data: any;
  dataPagePolicy: any;
  contactUsData?: {
    gameOptions: {label: string; value: string}[];
    platformOptions: {label: string; value: string}[];
  };
};

const Support: React.FC<SupportQExt> = ({data, dataPagePolicy, contactUsData}) => {
  const page = data?.pageCollection?.items[0];
  const {globalData} = usePageSetting();
  const dataTranslation = globalData?.dataTranslation?.[0] ?? {};
  if (!page) {
    return <ErrorPage statusCode={404} />;
  }
  const block: Array<any> = [page.sectionCollection?.items, page.seoStandard].filter(
    block => !!block
  );

  const blockCopy = cloneDeep(block);

  blockCopy[0]?.map((section: any, idx: number) => {
    if (section?.blocksCollection?.items[0]?.__typename === 'SupportFaq') {
      blockCopy[0][idx].blocksCollection.items[0] = {
        ...blockCopy[0][idx].blocksCollection.items[0],
        dataPagePolicy: dataPagePolicy ?? [],
        title: dataTranslation['support-header'],
        secondaryTitle: dataTranslation['support-title-our-terms-and-policies'],
        ordersLabel: dataTranslation['support-tabs-orders'],
        shippingAndDeliveryLabel: dataTranslation['support-tabs-shipping-and-delivery'],
        returnAndRefundsLabel: dataTranslation['support-tabs-returns-and-refunds'],
        disscountCodeLabel: dataTranslation['support-tabs-discount-codes'],
        popularQuestionsLabel: dataTranslation['support-title-popular-questions'],
        supportInfoTitle: dataTranslation['support-title-have-other-questions'],
        supportButtonLabel: dataTranslation['support-title-contact-us'],
        __typename: 'SupportFaq',
      };
    }
    // Inject server-side fetched game/platform data into ContactUs Global block
    if (
      section?.blocksCollection?.items[0]?.__typename === 'Global' &&
      section?.blocksCollection?.items[0]?.type === 'ContactUs' &&
      contactUsData
    ) {
      blockCopy[0][idx].blocksCollection.items[0] = {
        ...blockCopy[0][idx].blocksCollection.items[0],
        configuration: {
          ...blockCopy[0][idx].blocksCollection.items[0].configuration,
          serverGameOptions: contactUsData.gameOptions,
          serverPlatformOptions: contactUsData.platformOptions,
        },
      };
    }
  });

  // const subscribe = {
  //   __typename: 'SubscribePopup',
  // };

  // Handle remove section when empty data
  const handleRemoveSectionWhenEmptyItem = () => {
    blockCopy[0].map((blockItem: any, idx: number) => {
      if (blockItem?.blocksCollection?.items[0]?.listItemCollection?.items?.length <= 0) {
        blockCopy[0].splice(idx, 1);
      }
    });
  };
  handleRemoveSectionWhenEmptyItem();

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'hidden',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <BlockRenderer block={[...blockCopy]} />
    </Box>
  );
};

export const getStaticProps = async (ctx: GetStaticPropsContext) => {
  const preview = !!(process && process.env && process.env.NEXT_PUBLIC_PREVIEW);
  const locale = handleConvertToLocaleContentful(ctx.locale as string);

  const [[pageData, dataPagePolicy], [navGameLatest, navOurPicks], [gameTitles, platformFilters]] =
    await Promise.all([
      Promise.all([
        getPageData({slug: 'support', preview}, ctx as any),
        getGloablByType(locale, GLOBAL_TYPE.listPagePolicy),
      ]),
      Promise.all([
        getListGameForNav(locale, 0, 6, {}, 'releaseDate_DESC'),
        getLitGameByIdBlock(
          LIST_ID_GAME_OUR_PICK[locale] ?? LIST_ID_GAME_OUR_PICK['en-sg'],
          locale
        ),
      ]),
      Promise.all([
        getListTitleGame(locale),
        getParamFilterBy(locale, {AND: [{typeFilter_contains: 'platform'}]}),
      ]),
    ]);

  const navData = {
    gameLatest: navGameLatest?.items ?? [],
    ourPicks: navOurPicks ?? [],
  };

  const gameOptions = (gameTitles?.items ?? []).map((item: any) => ({
    label: item?.title || '',
    value: item?.title || '',
  }));
  const platformOptions = (platformFilters?.items ?? []).map((item: any) => ({
    label: item?.title || '',
    value: item?.title || '',
  }));

  const {data} = pageData;

  return {
    props: {
      data,
      locale: ctx.locale,
      dataPagePolicy,
      navData,
      contactUsData: {
        gameOptions,
        platformOptions,
      },
    },
    revalidate: REVALIDATE_TIME,
  };
};

export default Support;

export const config = {
  api: {
    bodyParser: false,
  },
};
